import React from 'react';
import {
  Box,
  ListItem,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  StarBorder as StarBorderIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';

const TaskListItem = React.memo(({
  task,
  index,
  draggedIndex,
  showDropGap,
  sortedActiveTasksLength,
  completedTasks,
  theme,
  getCategoryIcon,
  getCategoryName,
  getDueDateDisplay,
  getDueDateColor,
  handleToggleStatus,
  handleToggleFocus,
  handleDelete,
  handleOpenDialog,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
  handleContextMenu,
}) => {
  const isDragging = draggedIndex === index;
  const hasCompletionAnimation = completedTasks[task._id] && task.status === 'completed';

  return (
    <Box
      sx={{
        position: 'relative',
        mb: showDropGap?.index === index && showDropGap?.position === 'above' ? 6 : 2,
        transition: 'margin-bottom 0.3s ease',
        opacity: isDragging ? 0.3 : 1,
      }}
    >
      {/* Drop gap indicator - above (only if not at the very top) */}
      {showDropGap?.index === index && showDropGap?.position === 'above' && index !== 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: -24,
            left: 0,
            right: 0,
            height: '16px',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '8px',
            zIndex: 10,
            opacity: 0.6,
            boxShadow: `0 0 8px ${theme.palette.primary.main}`,
          }}
        />
      )}
      
      <ListItem
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, index)}
        onDragEnd={handleDragEnd}
        onContextMenu={(e) => handleContextMenu(e, task)}
        onClick={(e) => {
          if (!e.target.closest('button')) {
            handleOpenDialog(task);
          }
        }}
        sx={{
          bgcolor: '#ffffff',
          borderRadius: '14px',
          boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.04)',
          border: '1.5px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          px: 5,
          py: 4,
          mb: 2.5,
          transition: 'all 80ms ease-out',
          '&:hover': {
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.06)',
            transform: 'translateY(-2px)',
            borderColor: 'rgba(0, 0, 0, 0.09)',
            background: '#ffffff',
            '& .delete-button': {
              opacity: 1,
            },
          },
          '& .delete-button': {
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 150ms ease-out',
          },
          '&:hover .delete-button': {
            opacity: 1,
            pointerEvents: 'auto',
            transition: 'opacity 150ms ease-out',
          },
          alignItems: 'center',
          cursor: 'grab',
          display: 'flex',
          gap: 3,
          opacity: isDragging ? 0.3 : 1,
          '&:active': {
            cursor: 'grabbing',
            transform: 'scale(0.99)',
          },
          userSelect: 'none',
          position: 'relative',
          // Completion animation
          ...(hasCompletionAnimation && {
            animation: 'completePulse 0.5s ease-out',
            '@keyframes completePulse': {
              '0%': { transform: 'scale(1)', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)' },
              '50%': { transform: 'scale(1.01)', boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)' },
              '100%': { transform: 'scale(1)', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)' },
            },
          }),
        }}
      >
        <IconButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToggleStatus(task._id, task.status, e);
          }}
          color={task.status === 'completed' ? 'success' : 'default'}
          sx={{ 
            transition: 'all 0.2s ease-out',
            '&:hover': { 
              transform: 'scale(1.1)',
              opacity: 0.8,
            },
          }}
          size="small"
        >
          {task.status === 'completed' ? <CheckCircleIcon fontSize="small" /> : <UncheckedIcon fontSize="small" />}
        </IconButton>
        <IconButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToggleFocus(task);
          }}
          sx={{
            color: task.focus ? 'warning.main' : 'text.secondary',
            transition: 'all 0.2s ease-out',
            '&:hover': { 
              color: 'warning.main',
              bgcolor: 'rgba(255,193,7,0.06)',
              transform: 'scale(1.1)',
            },
          }}
          size="small"
        >
          {task.focus ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              textDecoration: task.status === 'completed' ? 'line-through' : 'none',
              color: theme.palette.text.primary,
              wordBreak: 'break-word',
              mb: 1,
              opacity: task.status === 'completed' ? 0.6 : 1,
            }}
          >
            {task.title}
          </Typography>
          {task.description && (
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                wordBreak: 'break-word',
                fontSize: '14px',
                color: theme.palette.text.secondary,
                opacity: task.status === 'completed' ? 0.6 : 1,
              }}
            >
              {task.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {task.dueDate && (
              <Typography variant="caption" sx={{ color: getDueDateColor(task.dueDate), fontSize: '13px', fontWeight: 400 }}>
                {getDueDateDisplay(task.dueDate)}
              </Typography>
            )}
            {task.recurring?.enabled && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RepeatIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: '14px' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400 }}>
                  {task.recurring.frequency === 'custom' 
                    ? `Every ${task.recurring.customValue} ${task.recurring.customUnit}`
                    : task.recurring.frequency === 'weekly' && task.recurring.weeklyDays && task.recurring.weeklyDays.length > 0
                    ? `Weekly (${task.recurring.weeklyDays.map(day => day.charAt(0).toUpperCase() + day.slice(1, 3)).join(', ')})`
                    : task.recurring.frequency.charAt(0).toUpperCase() + task.recurring.frequency.slice(1)
                  }
                </Typography>
              </Box>
            )}
            {task.category && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {(() => {
                  const IconComponent = getCategoryIcon(task.category);
                  return IconComponent ? React.createElement(IconComponent, { fontSize: 'small', sx: { fontSize: '14px', color: 'text.secondary' } }) : null;
                })()}
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400 }}>
                  {getCategoryName(task.category)}
                </Typography>
              </Box>
            )}
            <Chip
              label={task.priority}
              size="small"
              sx={{ 
                height: 22,
                fontSize: '11px',
                fontWeight: 500,
                borderRadius: '999px',
                px: 1.5,
                ...(task.priority === 'high' && {
                  bgcolor: '#F8D4D4',
                  color: '#8B2E2E',
                }),
                ...(task.priority === 'medium' && {
                  bgcolor: '#FCEED4',
                  color: '#8B5A2B',
                }),
                ...(task.priority === 'low' && {
                  bgcolor: '#E4F3FD',
                  color: '#1E4A72',
                }),
                '& .MuiChip-label': {
                  px: 1.5,
                },
              }}
            />
          </Box>
        </Box>
        <IconButton
          type="button"
          edge="end"
          className="delete-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDelete(task._id);
          }}
          sx={{
            color: 'grey.500',
            ml: 1,
            transition: 'color 0.2s',
            '&:hover': { color: 'error.main', bgcolor: 'rgba(244,67,54,0.08)' },
          }}
        >
          <DeleteIcon />
        </IconButton>
      </ListItem>
      
      {/* Drop gap indicator - below (only if not at the very bottom) */}
      {showDropGap?.index === index && showDropGap?.position === 'below' && index !== sortedActiveTasksLength - 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -24,
            left: 0,
            right: 0,
            height: '16px',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '8px',
            zIndex: 10,
            opacity: 0.6,
            boxShadow: `0 0 8px ${theme.palette.primary.main}`,
          }}
        />
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  return (
    prevProps.task._id === nextProps.task._id &&
    prevProps.task.status === nextProps.task.status &&
    prevProps.task.focus === nextProps.task.focus &&
    prevProps.task.title === nextProps.task.title &&
    prevProps.task.dueDate === nextProps.task.dueDate &&
    prevProps.task.priority === nextProps.task.priority &&
    prevProps.task.category?.toString() === nextProps.task.category?.toString() &&
    prevProps.task.recurring?.enabled === nextProps.task.recurring?.enabled &&
    prevProps.task.recurring?.frequency === nextProps.task.recurring?.frequency &&
    prevProps.index === nextProps.index &&
    prevProps.draggedIndex === nextProps.draggedIndex &&
    prevProps.showDropGap?.index === nextProps.showDropGap?.index &&
    prevProps.showDropGap?.position === nextProps.showDropGap?.position &&
    prevProps.completedTasks[prevProps.task._id] === nextProps.completedTasks[nextProps.task._id]
  );
});

TaskListItem.displayName = 'TaskListItem';

export default TaskListItem;

